import React, { useRef, useState } from "react";
import { CreateCommanWay, GetCommanWay, ListCommanWay, UpdateCommanWay } from "API/commonApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import Swal from "sweetalert2";
import UseToast from "utils/AfToast";
import Select from "react-select";
import Spinner from "utils/Spinner";
import { Button, Card, CardBody, Chip, Checkbox } from "@heroui/react";
import {
  UserGroupIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  ArrowPathIcon,
  BuildingOfficeIcon,
  NoSymbolIcon,
  SparklesIcon,
  UserIcon,
  DocumentArrowDownIcon,
} from "@heroicons/react/24/outline";
import { utils, write } from "xlsx";
import { saveAs } from "file-saver";
import { roles } from "utils/constant";

const pickupSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    background: "rgba(255, 255, 255, 0.95)",
    borderRadius: "10px",
    borderColor: state.isFocused ? "#0077B6" : "#cbd5e1",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 119, 182, 0.2)" : "none",
    padding: "0px 4px",
    minHeight: "36px",
    fontSize: "13px",
    transition: "all 0.2s ease",
    "&:hover": { borderColor: "#0077B6" },
  }),
  valueContainer: (base: any) => ({ ...base, padding: "0 8px" }),
  input: (base: any) => ({ ...base, margin: "0px", padding: "0px" }),
  indicatorsContainer: (base: any) => ({ ...base, height: "36px" }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected ? "#0077B6" : state.isFocused ? "#e0f2fe" : "white",
    color: state.isSelected ? "white" : "#1e293b",
    fontSize: "13px",
    padding: "6px 12px",
    cursor: "pointer",
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: "10px",
    overflow: "hidden",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
    zIndex: 99999,
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),
  placeholder: (base: any) => ({ ...base, color: "#94a3b8", fontSize: "13px" }),
  singleValue: (base: any) => ({ ...base, color: "#1e293b", fontSize: "13px", fontWeight: "500" }),
};

const getFinancialYear = (date?: any) => {
  const m = date ? moment(date) : moment();
  const year = m.year();
  const month = m.month();
  if (month >= 3) {
    return `${year}-${year + 1}`;
  } else {
    return `${year - 1}-${year}`;
  }
};

const getFinancialYearOptions = () => {
  const currentFY = getFinancialYear();
  const currentStartYear = parseInt(currentFY.split("-")[0]);
  return [
    { value: `${currentStartYear + 1}-${currentStartYear + 2}`, label: `FY ${currentStartYear + 1}-${currentStartYear + 2}` },
    { value: `${currentStartYear}-${currentStartYear + 1}`, label: `FY ${currentStartYear}-${currentStartYear + 1} (Current)` },
    { value: `${currentStartYear - 1}-${currentStartYear}`, label: `FY ${currentStartYear - 1}-${currentStartYear}` },
    { value: `${currentStartYear - 2}-${currentStartYear - 1}`, label: `FY ${currentStartYear - 2}-${currentStartYear - 1}` },
  ];
};

const EmployeeQuotas: React.FC = () => {
  const userD = (() => {
    const auth = localStorage.getItem("auth");
    return auth ? JSON.parse(auth) : {};
  })();
  const userDetails = userD?.state?.user || {};

  // Strictly SuperAdmin only
  if (userDetails.roleName !== roles.SUPER_ADMIN) {
    return (
      <div className="p-8 text-center">
        <div className="max-w-md mx-auto bg-white p-6 rounded-2xl border border-red-100 shadow-sm text-center">
          <NoSymbolIcon className="w-12 h-12 text-red-500 mx-auto mb-2" />
          <h2 className="text-base font-bold text-slate-800">Access Denied</h2>
          <p className="text-xs text-slate-500 mt-1">This page is restricted to Super Admin only.</p>
        </div>
      </div>
    );
  }

  const [selectedFY, setSelectedFY] = useState<any>({
    value: getFinancialYear(),
    label: `FY ${getFinancialYear()} (Current)`,
  });

  const [selectedBranch, setSelectedBranch] = useState<any>(null);
  const [branchOptions, setBranchOptions] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const perPage = 30;

  const tableRef = useRef<HTMLTableElement>(null);

  // Edit Quota Modal state
  const [editQuotaModal, setEditQuotaModal] = useState<any>(null);
  const [editForm, setEditForm] = useState<any>({
    casualTotal: 0,
    sickTotal: 0,
    paidTotal: 0,
    maternityTotal: 0,
    isMaternityEligible: false,
    isCompOffEligible: false,
    remarks: "",
  });
  const [isUpdatingQuota, setIsUpdatingQuota] = useState<boolean>(false);

  // Credit Comp-Off Modal state
  const [isCompOffModalOpen, setIsCompOffModalOpen] = useState<boolean>(false);
  const [compOffForm, setCompOffForm] = useState<any>({
    user: null,
    days: 1,
    workedDate: moment().format("YYYY-MM-DD"),
    reason: "",
  });
  const [isCreditingCompOff, setIsCreditingCompOff] = useState<boolean>(false);

  // Bulk Generate Modal state
  const [isBulkModalOpen, setIsBulkModalOpen] = useState<boolean>(false);
  const [bulkForm, setBulkForm] = useState<any>({
    financialYear: getFinancialYear(),
    policyId: null,
    branchId: null,
    overrideExisting: false,
  });
  const [policyOptions, setPolicyOptions] = useState<any[]>([]);
  const [isBulkGenerating, setIsBulkGenerating] = useState<boolean>(false);

  // Fetch Branches
  useQuery(
    ["subbranchListForQuotas"],
    () => ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000 }),
    {
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS") {
          const list = res?.data?.data || res?.data?.docs || [];
          setBranchOptions(list.map((b: any) => ({ value: b.id, label: b.branch_name || b.name || `#${b.id}` })));
        }
      },
    }
  );

  // Fetch Users for Filter
  useQuery(
    ["userListForQuotasFilter"],
    () => ListCommanWay("/admin/user/list", { isActive: true, isThirdParty: false }, { paginate: 2000 }),
    {
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS") {
          const list = res?.data?.data || res?.data?.docs || [];
          setUserOptions(
            list.map((u: any) => ({
              value: u.id,
              label: `${u.name || u.username} (${u.email || u.mobile || "#" + u.id})`,
            }))
          );
        }
      },
    }
  );

  // Fetch Policies for Bulk Generate
  useQuery(
    ["policyListForBulk"],
    () => ListCommanWay("/admin/leave-policy/list", { isActive: true }, { paginate: 100 }),
    {
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS") {
          const list = res?.data?.data || res?.data?.docs || [];
          setPolicyOptions(list.map((p: any) => ({ value: p.id, label: `${p.name} (${p.code || "Standard"})` })));
        }
      },
    }
  );

  // Build Query for Quotas List
  const buildQuery = () => {
    const q: any = {};
    if (selectedFY?.value) q.financialYear = selectedFY.value;
    if (selectedBranch?.value) {
      q.subbranch = selectedBranch.value;
      q.branchId = selectedBranch.value;
    }
    if (selectedUser?.value) q.user = selectedUser.value;
    return q;
  };

  // Fetch Quotas List
  const {
    data: quotasData,
    isLoading,
    isFetching,
    refetch,
  } = useQuery(
    ["leaveQuotasList", selectedFY?.value, selectedBranch?.value, selectedUser?.value, currentPage],
    () =>
      ListCommanWay("/admin/leave-quota/list", buildQuery(), {
        page: currentPage,
        paginate: perPage,
        sort: { createdAt: -1 },
      }),
    {
      keepPreviousData: true,
    }
  );

  const quotasList = quotasData?.data?.data || quotasData?.data?.docs || [];
  const paginator = quotasData?.data?.paginator || {
    itemCount: quotasList.length,
    pageCount: Math.ceil(quotasList.length / perPage) || 1,
    currentPage: 1,
  };

  // Open Edit Modal
  const handleOpenEdit = (item: any) => {
    setEditQuotaModal(item);
    setEditForm({
      casualTotal: item.casualTotal ?? 0,
      sickTotal: item.sickTotal ?? 0,
      paidTotal: item.paidTotal ?? 0,
      maternityTotal: item.maternityTotal ?? 0,
      isMaternityEligible: !!item.isMaternityEligible,
      isCompOffEligible: !!item.isCompOffEligible,
      remarks: "",
    });
  };

  // Submit Edit Quota
  const handleSaveEditQuota = async (e?: any) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    if (!editQuotaModal?.id) return;

    setIsUpdatingQuota(true);
    try {
      const payload = {
        casualTotal: Number(editForm.casualTotal),
        sickTotal: Number(editForm.sickTotal),
        paidTotal: Number(editForm.paidTotal),
        maternityTotal: Number(editForm.maternityTotal),
        isMaternityEligible: Boolean(editForm.isMaternityEligible),
        isCompOffEligible: Boolean(editForm.isCompOffEligible),
        remarks: editForm.remarks?.trim() || "Adjusted by Super Admin",
      };

      const res: any = await UpdateCommanWay({
        url: `/admin/leave-quota/update/${editQuotaModal.id}`,
        data: payload,
      });

      if (res?.status === "SUCCESS") {
        UseToast("Employee quota updated successfully", "success");
        setEditQuotaModal(null);
        refetch();
      } else {
        UseToast(res?.message || "Failed to update quota", "error");
      }
    } catch (err: any) {
      UseToast(err?.message || "Error updating quota", "error");
    } finally {
      setIsUpdatingQuota(false);
    }
  };

  // Submit Credit Comp-Off
  const handleCreditCompOffSubmit = async (e?: any) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    if (!compOffForm.user?.value) {
      UseToast("Please select employee", "warning");
      return;
    }
    if (!compOffForm.days || Number(compOffForm.days) <= 0) {
      UseToast("Please enter valid days", "warning");
      return;
    }
    if (!compOffForm.reason?.trim()) {
      UseToast("Please enter reason", "warning");
      return;
    }

    setIsCreditingCompOff(true);
    try {
      const payload = {
        user: compOffForm.user.value,
        days: Number(compOffForm.days),
        workedDate: compOffForm.workedDate,
        reason: compOffForm.reason.trim(),
      };

      const res: any = await CreateCommanWay({
        url: "/admin/leave-quota/credit-comp-off",
        data: payload,
      });

      if (res?.status === "SUCCESS") {
        UseToast("Comp-Off credited successfully!", "success");
        setIsCompOffModalOpen(false);
        setCompOffForm({
          user: null,
          days: 1,
          workedDate: moment().format("YYYY-MM-DD"),
          reason: "",
        });
        refetch();
      } else {
        UseToast(res?.message || "Failed to credit comp-off", "error");
      }
    } catch (err: any) {
      UseToast(err?.message || "Error crediting comp-off", "error");
    } finally {
      setIsCreditingCompOff(false);
    }
  };

  // Submit Bulk Generate
  const handleBulkGenerateSubmit = async (e?: any) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    if (!bulkForm.financialYear) {
      UseToast("Please select Financial Year", "warning");
      return;
    }

    Swal.fire({
      title: `Bulk Generate Quotas for FY ${bulkForm.financialYear}?`,
      text: bulkForm.overrideExisting
        ? "Warning: Override Existing is ENABLED. Existing quota records will be overwritten!"
        : "This will initialize quotas for all employees for the selected Financial Year.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#003995",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Generate Quotas",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsBulkGenerating(true);
        try {
          const payload = {
            financialYear: bulkForm.financialYear,
            policyId: bulkForm.policyId?.value || null,
            subbranch: bulkForm.branchId?.value || null,
            branchId: bulkForm.branchId?.value || null,
            overrideExisting: Boolean(bulkForm.overrideExisting),
          };

          const res: any = await CreateCommanWay({
            url: "/admin/leave-quota/bulk-generate",
            data: payload,
          });

          if (res?.status === "SUCCESS") {
            UseToast("Bulk FY quotas generated successfully!", "success");
            setIsBulkModalOpen(false);
            refetch();
          } else {
            UseToast(res?.message || "Failed to generate bulk quotas", "error");
          }
        } catch (err: any) {
          UseToast(err?.message || "Error during bulk generation", "error");
        } finally {
          setIsBulkGenerating(false);
        }
      }
    });
  };

  // Export to Excel
  const exportToExcel = () => {
    if (!tableRef.current) return;
    const tableClone = tableRef.current.cloneNode(true) as HTMLTableElement;
    const toIgnore = tableClone.querySelectorAll(".ignore-excel");
    toIgnore.forEach((el) => el.remove());

    const ws = utils.table_to_sheet(tableClone);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Employee Quotas");
    const wbout = write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, `Employee_Quotas_${selectedFY?.value}.xlsx`);
  };

  return (
    <div className="bg-gradient-to-br from-purple-400 via-blue-50 to-primary/50 py-2 px-2 font-inter min-h-screen">
      {(isLoading || isFetching) && <Spinner />}

      {/* Header Bar */}
      <div className="mx-auto mb-2">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-3 py-2 shadow-xl shadow-slate-200/50">
          <div className="flex items-center justify-between">
            <div className="bg-blue-100 text-primary px-3 py-1 rounded-lg text-sm font-semibold">
              Total: {paginator.itemCount} Employees
            </div>

            <h1 className="text-base font-bold bg-gradient-to-r from-primary to-amber-600 bg-clip-text text-transparent uppercase">
              EMPLOYEE QUOTAS
            </h1>

            <div className="flex items-center gap-2">
              <div className="w-44">
                <Select
                  options={getFinancialYearOptions()}
                  value={selectedFY}
                  onChange={setSelectedFY}
                  styles={pickupSelectStyles}
                  isSearchable={false}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                />
              </div>

              <Button
                size="sm"
                variant="bordered"
                isIconOnly
                onPress={() => refetch()}
                className="h-8 w-8 bg-white/90 rounded-lg text-slate-700"
                title="Refresh List"
              >
                <ArrowPathIcon className="w-4 h-4" />
              </Button>

              <Button
                size="sm"
                color="secondary"
                variant="flat"
                onPress={() => setIsCompOffModalOpen(true)}
                startContent={<PlusCircleIcon className="w-4 h-4" />}
                className="h-8 px-3 text-xs font-semibold rounded-lg"
              >
                Credit Comp-Off
              </Button>

              <Button
                size="sm"
                color="primary"
                onPress={() => setIsBulkModalOpen(true)}
                startContent={<SparklesIcon className="w-4 h-4" />}
                className="h-8 px-3 text-xs font-semibold rounded-lg bg-primary text-white shadow hover:bg-primary/90"
              >
                Bulk Generate FY
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="mx-auto mb-3">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-5 py-4 shadow-xl shadow-slate-200/50">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex flex-wrap items-end gap-3">
              {/* Branch Filter */}
              <div className="w-52">
                <label className="block mb-1 text-xs font-semibold text-slate-700">Branch</label>
                <Select
                  options={[{ value: null, label: "All Branches" }, ...branchOptions]}
                  value={selectedBranch}
                  onChange={(e) => {
                    setSelectedBranch(e);
                    setCurrentPage(1);
                  }}
                  placeholder="Select Branch"
                  isClearable
                  styles={pickupSelectStyles}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                />
              </div>

              {/* Employee Filter */}
              <div className="w-64">
                <label className="block mb-1 text-xs font-semibold text-slate-700">Employee</label>
                <Select
                  options={[{ value: null, label: "All Employees" }, ...userOptions]}
                  value={selectedUser}
                  onChange={(e) => {
                    setSelectedUser(e);
                    setCurrentPage(1);
                  }}
                  placeholder="Select Employee"
                  isClearable
                  styles={pickupSelectStyles}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                />
              </div>
            </div>

            <Button
              size="sm"
              variant="flat"
              color="primary"
              className="h-9 px-4 rounded-xl text-xs font-bold"
              startContent={<DocumentArrowDownIcon className="w-4 h-4" />}
              onPress={exportToExcel}
            >
              Export Excel
            </Button>
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="mx-auto mb-3">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl shadow-slate-200/50 p-4 overflow-hidden">
          {quotasList.length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              <UserGroupIcon className="w-14 h-14 mx-auto text-slate-300 mb-2 stroke-1" />
              <p className="text-base font-semibold text-slate-600">No quota records found for FY {selectedFY?.value}</p>
              <p className="text-xs text-slate-400 mt-1">
                Click <strong>Bulk Generate FY</strong> to initialize annual quotas for employees.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[70vh]">
              <table ref={tableRef} className="w-full text-left text-sm">
                <thead className="bg-slate-100/90 text-slate-800 font-bold uppercase tracking-wider text-xs sticky top-0 z-10 backdrop-blur-sm border-b border-slate-200">
                  <tr>
                    <th className="py-3 px-4">Employee</th>
                    <th className="py-3 px-4">Branch</th>
                    <th className="py-3 px-4 text-center">Casual (CL)</th>
                    <th className="py-3 px-4 text-center">Sick (SL)</th>
                    <th className="py-3 px-4 text-center">Paid (PL)</th>
                    <th className="py-3 px-4 text-center">Comp-Off</th>
                    <th className="py-3 px-4 text-center">Maternity</th>
                    <th className="py-3 px-4 text-center">Paid Leaves Left</th>
                    <th className="py-3 px-4 text-right ignore-excel">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-800">
                  {quotasList.map((item: any) => {
                    const empName = item._user?.name || item.user_data?.name || item._user?.username || item.user_data?.username || `User #${item.user}`;
                    const empEmail = item._user?.email || item.user_data?.email || item._user?.mobile || item.user_data?.mobile || (item._user?.empCode ? `Code: ${item._user.empCode}` : "");
                    const branchName = item._user?._subbranch?.branch_name || item._user?._branch?.name || item._user?.branch_name || item.user_data?.subbranch_data?.branch_name || item.user_data?.branch || "—";
                    return (
                      <tr key={item.id} className="hover:bg-blue-50/50 transition-colors">
                        <td className="py-3 px-4 whitespace-nowrap">
                          <div className="font-bold text-slate-900 flex items-center gap-1.5 text-sm">
                            <UserIcon className="w-4 h-4 text-slate-400" />
                            <span>{empName}</span>
                            {item.isProrated && (
                              <span className="text-xs bg-purple-100 text-purple-800 font-bold px-2 py-0.5 rounded">
                                Prorated
                              </span>
                            )}
                          </div>
                          {empEmail && <div className="text-xs text-slate-500 font-medium">{empEmail}</div>}
                        </td>

                        <td className="py-3 px-4 whitespace-nowrap text-slate-700 font-medium">
                          {branchName}
                        </td>

                        {/* Casual */}
                        <td className="py-3 px-4 text-center whitespace-nowrap">
                          <strong className="text-blue-950 text-base">{item.casualRemaining ?? 0}</strong>
                          <span className="text-xs text-slate-500"> / {item.casualTotal ?? 0}</span>
                          <div className="text-xs text-slate-400 font-medium">Used: {item.casualUsed ?? 0}</div>
                        </td>

                        {/* Sick */}
                        <td className="py-3 px-4 text-center whitespace-nowrap">
                          <strong className="text-emerald-950 text-base">{item.sickRemaining ?? 0}</strong>
                          <span className="text-xs text-slate-500"> / {item.sickTotal ?? 0}</span>
                          <div className="text-xs text-slate-400 font-medium">Used: {item.sickUsed ?? 0}</div>
                        </td>

                        {/* Paid */}
                        <td className="py-3 px-4 text-center whitespace-nowrap">
                          <strong className="text-purple-950 text-base">{item.paidRemaining ?? 0}</strong>
                          <span className="text-xs text-slate-500"> / {item.paidTotal ?? 0}</span>
                          <div className="text-xs text-slate-400 font-medium">Used: {item.paidUsed ?? 0}</div>
                        </td>

                        {/* Comp-Off */}
                        <td className="py-3 px-4 text-center whitespace-nowrap">
                          {item.isCompOffEligible ? (
                            <>
                              <strong className="text-amber-950 text-base">{item.compOffRemaining ?? 0}</strong>
                              <span className="text-xs text-slate-500"> / {item.compOffEarned ?? 0}</span>
                              <div className="text-xs text-slate-400 font-medium">Used: {item.compOffUsed ?? 0}</div>
                            </>
                          ) : (
                            <span className="text-xs text-slate-400 italic font-medium">Not Eligible</span>
                          )}
                        </td>

                        {/* Maternity */}
                        <td className="py-3 px-4 text-center whitespace-nowrap">
                          {item.isMaternityEligible ? (
                            <>
                              <strong className="text-pink-950 text-base">{item.maternityRemaining ?? 0}</strong>
                              <span className="text-xs text-slate-500"> / {item.maternityTotal ?? 0}</span>
                            </>
                          ) : (
                            <span className="text-xs text-slate-400 italic font-medium">Not Eligible</span>
                          )}
                        </td>

                        {/* Paid Leaves Left */}
                        <td className="py-3 px-4 text-center whitespace-nowrap">
                          <span className="px-3 py-1 rounded-full text-xs font-extrabold bg-emerald-100 text-emerald-900 border border-emerald-300">
                            {item.totalRemainingPaidLeaves ?? (item.casualRemaining || 0) + (item.sickRemaining || 0) + (item.paidRemaining || 0) + (item.compOffRemaining || 0)} Days
                          </span>
                        </td>

                        {/* Edit Action */}
                        <td className="py-3 px-4 text-right whitespace-nowrap ignore-excel">
                          <Button
                            size="sm"
                            variant="flat"
                            color="primary"
                            onPress={() => handleOpenEdit(item)}
                            startContent={<PencilSquareIcon className="w-4 h-4" />}
                            className="text-xs h-8 px-3 font-bold"
                          >
                            Edit
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {paginator.pageCount > 1 && (
            <div className="p-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-600 font-medium">
              <span>
                Page {paginator.currentPage || currentPage} of {paginator.pageCount} ({paginator.itemCount} records)
              </span>
              <div className="flex items-center gap-1.5">
                <Button
                  size="sm"
                  variant="flat"
                  isDisabled={currentPage <= 1}
                  onPress={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  className="h-8 text-xs font-semibold"
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="flat"
                  isDisabled={currentPage >= paginator.pageCount}
                  onPress={() => setCurrentPage((p) => Math.min(paginator.pageCount, p + 1))}
                  className="h-8 text-xs font-semibold"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Quota Modal */}
      {editQuotaModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-100">
            <div className="px-6 py-4 bg-gradient-to-r from-primary to-primary2 text-white flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <PencilSquareIcon className="w-5 h-5 text-blue-200" />
                <div>
                  <h3 className="text-base font-bold">
                    Edit Quota: {editQuotaModal.user_data?.name || `User #${editQuotaModal.user}`}
                  </h3>
                  <span className="text-xs text-blue-200 font-medium">{editQuotaModal.financialYear}</span>
                </div>
              </div>
              <button
                onClick={() => setEditQuotaModal(null)}
                className="p-1 rounded-lg text-blue-200 hover:text-white hover:bg-white/10"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveEditQuota} className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-700 font-bold mb-1 text-xs">Casual (CL)</label>
                  <input
                    type="number"
                    min={0}
                    value={editForm.casualTotal}
                    onChange={(e) => setEditForm({ ...editForm, casualTotal: e.target.value })}
                    className="w-full h-9 px-3 text-sm border border-slate-300 rounded-xl outline-none focus:border-blue-600 font-bold"
                    required
                  />
                </div>

                <div>
                  <label className="block text-slate-700 font-bold mb-1 text-xs">Sick (SL)</label>
                  <input
                    type="number"
                    min={0}
                    value={editForm.sickTotal}
                    onChange={(e) => setEditForm({ ...editForm, sickTotal: e.target.value })}
                    className="w-full h-9 px-3 text-sm border border-slate-300 rounded-xl outline-none focus:border-blue-600 font-bold"
                    required
                  />
                </div>

                <div>
                  <label className="block text-slate-700 font-bold mb-1 text-xs">Paid (PL)</label>
                  <input
                    type="number"
                    min={0}
                    value={editForm.paidTotal}
                    onChange={(e) => setEditForm({ ...editForm, paidTotal: e.target.value })}
                    className="w-full h-9 px-3 text-sm border border-slate-300 rounded-xl outline-none focus:border-blue-600 font-bold"
                    required
                  />
                </div>
              </div>

              {/* Comp-Off Eligibility */}
              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
                <div>
                  <span className="font-bold text-slate-900 block text-sm">Comp-Off Eligibility</span>
                  <p className="text-xs text-slate-500 font-medium">Allow staff to accumulate comp-off days</p>
                </div>
                <Checkbox
                  isSelected={editForm.isCompOffEligible}
                  onValueChange={(val) => setEditForm({ ...editForm, isCompOffEligible: val })}
                  size="md"
                  color="primary"
                />
              </div>

              {/* Maternity Eligibility */}
              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-2.5">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-bold text-slate-900 block text-sm">Maternity Leave</span>
                    <p className="text-xs text-slate-500 font-medium">Enable maternity leaves for this employee</p>
                  </div>
                  <Checkbox
                    isSelected={editForm.isMaternityEligible}
                    onValueChange={(val) => setEditForm({ ...editForm, isMaternityEligible: val })}
                    size="md"
                    color="primary"
                  />
                </div>

                {editForm.isMaternityEligible && (
                  <div className="pt-2.5 border-t border-slate-200">
                    <label className="block text-slate-700 font-bold mb-1 text-xs">Maternity Total Days</label>
                    <input
                      type="number"
                      min={0}
                      value={editForm.maternityTotal}
                      onChange={(e) => setEditForm({ ...editForm, maternityTotal: e.target.value })}
                      className="w-full h-9 px-3 text-sm border border-slate-300 rounded-xl outline-none focus:border-blue-600 font-bold bg-white"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-slate-700 font-bold mb-1 text-xs">Adjustment Remarks</label>
                <textarea
                  rows={2}
                  value={editForm.remarks}
                  onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })}
                  placeholder="e.g. Special adjustment approved by HR"
                  className="w-full p-2.5 text-sm border border-slate-300 rounded-xl outline-none focus:border-blue-600 resize-none font-medium"
                />
              </div>

              <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2.5">
                <Button
                  size="sm"
                  variant="flat"
                  onPress={() => setEditQuotaModal(null)}
                  isDisabled={isUpdatingQuota}
                  className="text-xs font-semibold"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  color="primary"
                  type="submit"
                  onPress={() => handleSaveEditQuota()}
                  isLoading={isUpdatingQuota}
                  className="text-xs font-bold bg-primary text-white"
                >
                  Save Changes
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Credit Comp-Off Modal */}
      {isCompOffModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-100">
            <div className="px-6 py-4 bg-gradient-to-r from-amber-700 to-amber-900 text-white flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <PlusCircleIcon className="w-5 h-5 text-amber-200" />
                <h3 className="text-base font-bold">Credit Comp-Off to Employee</h3>
              </div>
              <button
                onClick={() => setIsCompOffModalOpen(false)}
                className="p-1 rounded-lg text-amber-200 hover:text-white hover:bg-white/10"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreditCompOffSubmit} className="p-6 space-y-4 text-sm">
              <div>
                <label className="block text-slate-700 font-bold mb-1 text-xs">
                  Employee <span className="text-red-500">*</span>
                </label>
                <Select
                  options={userOptions}
                  value={compOffForm.user}
                  onChange={(e) => setCompOffForm({ ...compOffForm, user: e })}
                  placeholder="Select employee..."
                  styles={pickupSelectStyles}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-bold mb-1 text-xs">
                    Comp-Off Days <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min={0.5}
                    value={compOffForm.days}
                    onChange={(e) => setCompOffForm({ ...compOffForm, days: e.target.value })}
                    className="w-full h-9 px-3 text-sm border border-slate-300 rounded-xl outline-none focus:border-amber-600 font-bold"
                    required
                  />
                </div>

                <div>
                  <label className="block text-slate-700 font-bold mb-1 text-xs">
                    Worked Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={compOffForm.workedDate}
                    onChange={(e) => setCompOffForm({ ...compOffForm, workedDate: e.target.value })}
                    className="w-full h-9 px-3 text-sm border border-slate-300 rounded-xl outline-none focus:border-amber-600 bg-white font-medium"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-700 font-bold mb-1 text-xs">
                  Reason / Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={3}
                  value={compOffForm.reason}
                  onChange={(e) => setCompOffForm({ ...compOffForm, reason: e.target.value })}
                  placeholder="e.g. Worked full day on Independence Day holiday"
                  className="w-full p-2.5 text-sm border border-slate-300 rounded-xl outline-none focus:border-amber-600 resize-none font-medium"
                  required
                />
              </div>

              <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2.5">
                <Button
                  size="sm"
                  variant="flat"
                  onPress={() => setIsCompOffModalOpen(false)}
                  isDisabled={isCreditingCompOff}
                  className="text-xs font-semibold"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  color="warning"
                  type="submit"
                  onPress={() => handleCreditCompOffSubmit()}
                  isLoading={isCreditingCompOff}
                  className="text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 shadow"
                >
                  Credit Comp-Off
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Generate FY Quotas Modal */}
      {isBulkModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-100">
            <div className="px-6 py-4 bg-gradient-to-r from-primary to-primary2 text-white flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <SparklesIcon className="w-5 h-5 text-blue-200" />
                <h3 className="text-base font-bold">Bulk Generate / Rollover FY Quotas</h3>
              </div>
              <button
                onClick={() => setIsBulkModalOpen(false)}
                className="p-1 rounded-lg text-blue-200 hover:text-white hover:bg-white/10"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleBulkGenerateSubmit} className="p-6 space-y-4 text-sm">
              <div>
                <label className="block text-slate-700 font-bold mb-1 text-xs">
                  Target Financial Year <span className="text-red-500">*</span>
                </label>
                <Select
                  options={getFinancialYearOptions()}
                  value={{ value: bulkForm.financialYear, label: `FY ${bulkForm.financialYear}` }}
                  onChange={(e: any) => setBulkForm({ ...bulkForm, financialYear: e?.value })}
                  styles={pickupSelectStyles}
                  isSearchable={false}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                />
              </div>

              <div>
                <label className="block text-slate-700 font-bold mb-1 text-xs">
                  Policy Template <span className="text-slate-400 font-normal">(Optional)</span>
                </label>
                <Select
                  options={[{ value: null, label: "Default Policy (CL:12, SL:6, PL:15)" }, ...policyOptions]}
                  value={bulkForm.policyId}
                  onChange={(e) => setBulkForm({ ...bulkForm, policyId: e })}
                  placeholder="Select Policy..."
                  isClearable
                  styles={pickupSelectStyles}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                />
              </div>

              <div>
                <label className="block text-slate-700 font-bold mb-1 text-xs">
                  Branch Filter <span className="text-slate-400 font-normal">(Optional)</span>
                </label>
                <Select
                  options={[{ value: null, label: "All Branches" }, ...branchOptions]}
                  value={bulkForm.branchId}
                  onChange={(e) => setBulkForm({ ...bulkForm, branchId: e })}
                  placeholder="Select Branch..."
                  isClearable
                  styles={pickupSelectStyles}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                />
              </div>

              <div className="p-3.5 bg-amber-50 rounded-xl border border-amber-200/60 flex items-center justify-between">
                <div>
                  <span className="font-bold text-amber-950 block text-sm">Override Existing</span>
                  <p className="text-xs text-amber-800 font-medium">Overwrite quotas if already created for this FY</p>
                </div>
                <Checkbox
                  isSelected={bulkForm.overrideExisting}
                  onValueChange={(val) => setBulkForm({ ...bulkForm, overrideExisting: val })}
                  size="md"
                  color="warning"
                />
              </div>

              <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2.5">
                <Button
                  size="sm"
                  variant="flat"
                  onPress={() => setIsBulkModalOpen(false)}
                  isDisabled={isBulkGenerating}
                  className="text-xs font-semibold"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  color="primary"
                  type="submit"
                  onPress={() => handleBulkGenerateSubmit()}
                  isLoading={isBulkGenerating}
                  className="text-xs font-bold bg-primary text-white"
                >
                  Generate Quotas
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeQuotas;
